import { Dropdown } from '@getsynapse/design-system';
import BudgetPlanTable from 'Pages/BudgetsPage/tabs/Plan/PlanTable/BudgetPlanTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import TableSkeletonLoader from '../../../../Organisms/TableSkeletonLoader/TableSkeletonLoader';
import {
  changeBudgetStatus,
  getBudgetViewType,
  getLineItemStatus,
  getSelectedBudget,
  selectListBudget,
  setBudgetViewType,
} from '../../../../state/BudgetList/budgetListSlice';
import { SLICE_STATUS } from '../../../../utils/constants';
import {
  BudgetModel,
  BudgetStatus,
  BudgetStatusChangeRequest,
  Option,
} from '../../../../utils/customTypes';
import useBudgetViewOptions from '../../hooks/useBudgetViewOptions';
import {
  ActiveStatusTypeEnum,
  ChangeActiveStatusModal,
} from './components/modals/ChangeActiveStatusModal';
import Skelton from '../../../../Atoms/Skelton/Skelton';

const BudgetPlan = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedBudget = useSelector(getSelectedBudget);
  const [changeStatusBudgetInfo, setChangeStatusBudgetInfo] =
    useState<BudgetModel>();
  const budgetList = useSelector(selectListBudget);
  const selectedBudgetView = useSelector(getBudgetViewType);
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(window.location.search);
  const activeTab = queryParams.get('active');
  const listStatus = useSelector(getLineItemStatus);
  const viewOptions = useBudgetViewOptions();

  const statusOptions: Option[] = useMemo(
    () => [
      {
        value: BudgetStatus.DRAFT,
        label: intl.get('BUDGET_PAGE.STATUS_OPTIONS.DRAFT'),
      },
      {
        value: BudgetStatus.ACTIVE,
        label: intl.get('BUDGET_PAGE.STATUS_OPTIONS.ACTIVE'),
      },
      {
        value: BudgetStatus.ARCHIVED,
        label: intl.get('BUDGET_PAGE.STATUS_OPTIONS.ARCHIVED'),
      },
    ],
    []
  );

  useEffect(() => {
    if (activeTab) {
      dispatch(
        setBudgetViewType(
          viewOptions.find((option) => option.value === activeTab)
        )
      );
    } else if (!selectedBudgetView) {
      dispatch(setBudgetViewType(viewOptions[0]));
    }
  }, [activeTab, dispatch, viewOptions, selectedBudgetView]);

  const findByValue = useCallback(
    (value: string) => {
      let val = statusOptions.find((option) => option.value === value);
      return val?.label || '';
    },
    [statusOptions] // Recomputes only when statusOptions changes
  );

  useEffect(() => {
    if (selectedBudget?.status) {
      setSelectedStatusInfo({
        label: findByValue(selectedBudget.status),
        value: selectedBudget.status,
      });
    }
  }, [findByValue, selectedBudget]);

  const [activeStatusType, setActiveStatusType] =
    useState<ActiveStatusTypeEnum>(ActiveStatusTypeEnum.REMOVE);

  const [selectedStatusInfo, setSelectedStatusInfo] = useState<Option>({
    label: '',
    value: '',
  });

  const onBudgetViewChange = (option: Option) => {
    dispatch(setBudgetViewType(option));
    const tab = queryParams.get('tab');
    history.push(`${location.pathname}?tab=${tab}&active=${option.value}`);
  };

  const [displayActiveStatusModal, setDisplayActiveStatusModal] =
    useState<boolean>(false);

  function onStatusChange(status: Option) {
    if (status.value === BudgetStatus.ACTIVE) {
      const budgets = budgetList.find(
        (item) =>
          new Date(item.fiscalPeriodStart).getFullYear() ===
            new Date(selectedBudget?.fiscalPeriodStart!).getFullYear() &&
          item.status === BudgetStatus.ACTIVE
      );
      if (budgets) {
        setChangeStatusBudgetInfo(budgets);
        setStoredStatusInfo(status);
        setActiveStatusType(ActiveStatusTypeEnum.REPLACE);
        setDisplayActiveStatusModal(true);
      } else {
        const statusChangeRequest: BudgetStatusChangeRequest = {
          id: selectedBudget?.id!,
          status: BudgetStatus.ACTIVE,
        };

        dispatch(changeBudgetStatus(statusChangeRequest));
      }
    } else if (selectedStatusInfo.value === BudgetStatus.ACTIVE) {
      setChangeStatusBudgetInfo(selectedBudget);
      setStoredStatusInfo(status);
      setActiveStatusType(ActiveStatusTypeEnum.REMOVE);
      setDisplayActiveStatusModal(true);
    } else {
      const statusChangeRequest: BudgetStatusChangeRequest = {
        id: selectedBudget?.id!,
        status: status.value,
      };

      dispatch(changeBudgetStatus(statusChangeRequest));
      setDisplayActiveStatusModal(false);
    }
  }

  function onStatusChangeSuccess() {
    const statusChangeRequest: BudgetStatusChangeRequest = {
      id: selectedBudget?.id!,
      status: storedStatusInfo.value,
    };
    dispatch(changeBudgetStatus(statusChangeRequest));

    setDisplayActiveStatusModal(false);
    setStoredStatusInfo(statusOptions[0]);
  }

  const [storedStatusInfo, setStoredStatusInfo] = useState<Option>(
    statusOptions[0]
  );

  return (
    <div className='flex flex-col mt-2 h-full'>
      {listStatus === SLICE_STATUS.LOADING && (
        <div className='flex flex-row justify-between mb-4'>
          <Skelton className='w-36 h-12' />
          <Skelton className='w-36 h-12' />
        </div>
      )}
      {listStatus === SLICE_STATUS.SUCCESS && (
        <div className='flex flex-row justify-between mb-4'>
          <div>
            <Dropdown
              triggerProps={{
                'data-testid': 'budget-view-trigger',
              }}
              popperDiffWidth={true}
              options={viewOptions}
              values={[
                viewOptions.find(
                  (option) => option.value === selectedBudgetView?.value
                ),
              ]}
              onChange={onBudgetViewChange}
              placeholder='Team Viewer'
            />
          </div>
          <div>
            <Dropdown
              triggerProps={{
                'data-testid': 'budget-status-trigger',
              }}
              className='w-36'
              controlled={true}
              options={statusOptions}
              values={[
                {
                  label: selectedStatusInfo?.label,
                  value: selectedStatusInfo?.value,
                },
              ]}
              onChange={(option) => {
                onStatusChange(option);
              }}
            />
          </div>
        </div>
      )}
      {listStatus === SLICE_STATUS.LOADING && <TableSkeletonLoader />}
      {listStatus === SLICE_STATUS.SUCCESS && <BudgetPlanTable />}
      <ChangeActiveStatusModal
        isOpen={displayActiveStatusModal}
        closeModal={() => setDisplayActiveStatusModal(false)}
        onCancel={() => setDisplayActiveStatusModal(false)}
        budget={changeStatusBudgetInfo}
        type={activeStatusType}
        onChangeStatus={() => {
          onStatusChangeSuccess();
        }}
      />
    </div>
  );
};
export default BudgetPlan;
